import { db } from "../../firebase";
import {
  collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp
} from "firebase/firestore";
import {
  FEEDBACK_FETCH_REQUEST, FEEDBACK_FETCH_SUCCESS, FEEDBACK_FETCH_FAILURE,
  FEEDBACK_CREATE_REQUEST, FEEDBACK_CREATE_SUCCESS, FEEDBACK_CREATE_FAILURE,
  FEEDBACK_UPDATE_REQUEST, FEEDBACK_UPDATE_SUCCESS, FEEDBACK_UPDATE_FAILURE,
  FEEDBACK_DELETE_REQUEST, FEEDBACK_DELETE_SUCCESS, FEEDBACK_DELETE_FAILURE,
  FEEDBACK_RESET
} from "./FeedbackTypes";

// Reset feedback state
export const resetFeedback = () => ({ type: FEEDBACK_RESET });

// Fetch all feedback (admin view)
export const fetchFeedback = ({ status = null, questionId = null } = {}) => {
  return async (dispatch) => {
    try {
      dispatch({ type: FEEDBACK_FETCH_REQUEST });

      const feedbackRef = collection(db, "questionBankFeedback");
      const constraints = [orderBy("createdAt", "desc")];

      if (status) constraints.unshift(where("status", "==", status));
      if (questionId) constraints.unshift(where("questionId", "==", questionId));

      const q = query(feedbackRef, ...constraints);
      const snapshot = await getDocs(q);

      const feedback = snapshot.docs.map(doc => ({
        feedbackId: doc.id,
        ...doc.data()
      }));

      dispatch({ type: FEEDBACK_FETCH_SUCCESS, payload: feedback });
      return { success: true, feedback };
    } catch (error) {
      dispatch({ type: FEEDBACK_FETCH_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Create feedback report
export const createFeedback = (feedbackData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: FEEDBACK_CREATE_REQUEST });

      const feedbackRef = collection(db, "questionBankFeedback");
      const newFeedback = {
        // Question reference
        questionId: feedbackData.questionId,
        questionCode: feedbackData.questionCode || null,
        passageId: feedbackData.passageId || null,

        // Feedback details
        feedbackType: feedbackData.feedbackType, // 'typo', 'incorrect_answer', 'unclear_question', 'incorrect_explanation', 'other'
        description: feedbackData.description,

        // User info
        userHandle: feedbackData.userHandle,
        userName: feedbackData.userName || null,

        // Context
        testId: feedbackData.testId || null,

        // Status tracking
        status: 'pending', // 'pending', 'reviewed', 'resolved', 'dismissed'
        adminNotes: null,
        resolvedBy: null,
        resolvedAt: null,

        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(feedbackRef, newFeedback);

      const createdFeedback = {
        feedbackId: docRef.id,
        ...newFeedback,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: FEEDBACK_CREATE_SUCCESS, payload: createdFeedback });
      return { success: true, feedbackId: docRef.id };
    } catch (error) {
      dispatch({ type: FEEDBACK_CREATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Update feedback (admin action)
export const updateFeedback = (feedbackId, updateData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: FEEDBACK_UPDATE_REQUEST });

      const feedbackRef = doc(db, "questionBankFeedback", feedbackId);
      const dataToUpdate = {
        ...updateData,
        updatedAt: serverTimestamp()
      };

      // If status is being set to resolved, add resolved timestamp
      if (updateData.status === 'resolved') {
        dataToUpdate.resolvedAt = serverTimestamp();
      }

      await updateDoc(feedbackRef, dataToUpdate);

      dispatch({
        type: FEEDBACK_UPDATE_SUCCESS,
        payload: { feedbackId, ...dataToUpdate, updatedAt: new Date().toISOString() }
      });
      return { success: true };
    } catch (error) {
      dispatch({ type: FEEDBACK_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Bulk resolve feedback by question codes (used after spreadsheet re-upload)
export const bulkResolveFeedback = (questionCodes) => {
  return async (dispatch) => {
    try {
      if (!questionCodes || questionCodes.length === 0) {
        return { success: true, resolvedCount: 0 };
      }

      const feedbackRef = collection(db, "questionBankFeedback");
      let resolvedCount = 0;

      // Firestore 'in' queries support max 10 values, so batch them
      const batches = [];
      for (let i = 0; i < questionCodes.length; i += 10) {
        batches.push(questionCodes.slice(i, i + 10));
      }

      for (const batch of batches) {
        // Find pending or reviewed feedback for these question codes
        const q = query(
          feedbackRef,
          where("questionCode", "in", batch),
          where("status", "in", ["pending", "reviewed"])
        );
        const snapshot = await getDocs(q);

        for (const feedbackDoc of snapshot.docs) {
          await updateDoc(doc(db, "questionBankFeedback", feedbackDoc.id), {
            status: 'resolved',
            resolvedBy: 'bulk_upload',
            resolvedAt: serverTimestamp(),
            adminNotes: 'Auto-resolved after question update via spreadsheet upload',
            updatedAt: serverTimestamp()
          });
          resolvedCount++;
        }
      }

      return { success: true, resolvedCount };
    } catch (error) {
      console.error('bulkResolveFeedback error:', error);
      return { success: false, error: error.message };
    }
  };
};

// Delete feedback
export const deleteFeedback = (feedbackId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: FEEDBACK_DELETE_REQUEST });

      const feedbackRef = doc(db, "questionBankFeedback", feedbackId);
      await deleteDoc(feedbackRef);

      dispatch({ type: FEEDBACK_DELETE_SUCCESS, payload: feedbackId });
      return { success: true };
    } catch (error) {
      dispatch({ type: FEEDBACK_DELETE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};
